import { TreeSelect } from "primereact/treeselect";
import { useEffect, useState } from "react";
import ConditionalRender from "../../helpers/ConditionalRender";
import InstanceApi from "../../api/instance/InstanceApi";
import HostInstanceTypes from "../../types/HostInstanceType";
import HostApi from "../../api/host/HostApi";
import { stringFormatting } from "../../helpers/utils";
import InstanceTarget from "../../types/instance/InstanceTarget";
import HostTarget from "../../types/host/HostTarget";

function Metrics(props: {
    setAggregation: Function,
    aggregation: string,
    entityType: string,
    metricType: string,
    datasourceId: string | number,
    hostId: string | number,
    disabled: boolean,
    setStatistic: Function,
    statistic: string,
    statisticError: string,
    isDelete: boolean,
    instances: InstanceTarget[],
    hosts: HostTarget[],
    setUnits?: Function,
    scopeTypes?: string[] | null,
    [propName: string]: any
}) {
    const {
        entityType,
        metricType,
        disabled,
        setStatistic,
        statistic,
        setAggregation,
        setUnits,
        instances,
        hosts,
        datasourceId,
        hostId,
        isDelete,
        statisticError,
        aggregation,
        scopeTypes
    } = props

    const [metricsList, setMetricsList] = useState<any[]>([]);

    const [supportedInstances, setSupportedInstances] = useState<HostInstanceTypes[]>([]);
    const [supportedHosts, setSupportedHosts] = useState<HostInstanceTypes[]>([]);


    useEffect(() => {
        const getInstanceTypes = async() => {
            const instancesSupported = await InstanceApi.getInstanceTypes()
            setSupportedInstances(instancesSupported)
        }

        const getHostsTypes = async() => {
            const hostsSupported = await HostApi.getHostTypes()
            setSupportedHosts(hostsSupported)
        }
        entityType === 'host' ? void getHostsTypes() : void getInstanceTypes()

    }, [entityType, metricType, datasourceId, hostId]);


    const getSupportedTypesList = (list: any[]) => {
        const scoped = scopeTypes && scopeTypes.length > 0 ? new Set(scopeTypes) : null;

        if (entityType === 'datasource') {
            if (scoped) {
                return supportedInstances?.filter(supportedType => scoped.has(supportedType.type));
            }
            if (datasourceId) {
                // @ts-ignore
                const selectedInstance: InstanceTarget = instances.find(inst => inst.id === datasourceId)
                return supportedInstances?.filter(supportedType => supportedType.type === selectedInstance?.type);
            }
            return supportedInstances?.filter(supportedType =>
                list.some(stat => stat.type === supportedType.type)
            );
        }

        if (entityType === 'host') {
            if (scoped) {
                return supportedHosts.filter(supportedType => scoped.has(supportedType.type));
            }
            if (hostId) {
                // @ts-ignore
                const selectedHost: HostTarget = hosts.find(inst => inst.id === hostId)
                return supportedHosts.filter(supportedType => supportedType.type === selectedHost.type);
            }
            return supportedHosts.filter(supportedType =>
                list.some(stat => stat.type === supportedType.type)
            );
        }
        return []
    }

    useEffect(() => {
        const ready =
            entityType === 'host'
                ? supportedHosts.length > 0
                : entityType === 'datasource' && supportedInstances.length > 0;
        if (!ready) {
            setMetricsList([]);
            return;
        }

        let cancelled = false;

        const loadMetrics = async () => {
            let list: any[] = [];
            let metricTypes: any[] = [];
            if (entityType === 'datasource') {
                if (metricType === 'status') {
                    metricTypes = await InstanceApi.getInstanceStatusTypes()
                } else {
                    metricTypes = await InstanceApi.getInstanceStatisticTypes(metricType === 'activity')
                }
            } else {
			    if (metricType === 'status') {
                    metricTypes = await HostApi.getHostStatusTypes()
                } else {
                    metricTypes = await HostApi.getHostStatisticTypes()
                }
            }
            if (cancelled) {
                return;
            }
            const supportedMetrics = getSupportedTypesList(metricTypes)
            if (supportedMetrics.length && metricTypes.length) {
                list = supportedMetrics.map((metric: any) => {
                    return {
                        "key": metric.type,
                        "label": metric.display,
                        "selectable": false,
                        children: metricTypes.filter((item: any) => item.type === metric.type).map((item: any) => ({
                            key: item.statistic,
                            label: stringFormatting(item.statistic),
                            data: item.statistic,
                            statistic: item.statistic,
                            type: item.type,
                            units: item.units,
                            cumulative: item.cumulative,
                            aggregation: item.aggregation,
                            collect: item.collect,
                            description: item.description
                        }))
                    };
                })
            }
            setMetricsList(list)
        };

        void loadMetrics();
        return () => {
            cancelled = true;
        };
    }, [
        entityType,
        metricType,
        datasourceId,
        hostId,
        scopeTypes,
        supportedInstances,
        supportedHosts,
        instances,
        hosts
    ]);

    return (
        <>
            <div className="col-xs-12 col-md-3">
                <span className="required">
                    <label htmlFor="metric" className="reminder">Metric</label>
                </span>
            </div>
            <div className="col-xs-12 col-md-6 col-lg-3 d-flex ">
                <div className="mr-10" style={{ flex: '0 0 90px' }}>
                    <select
                        id="aggregation"
                        className={`form-control`}
                        value={aggregation}
                        disabled={true}
                        data-lpignore={true}
                    >
                        <option value="avg">Average</option>
                        <option value="sum">Sum</option>
                    </select>
                </div>
                <div className='mr-10' style={{ flex: '1 1 0' }}>
                    <TreeSelect value={statistic}
                                options={metricsList}
                                className={`form-control ${statistic && !isDelete ? 'is-valid' : statisticError === 'invalid' ? 'is-invalid' : ''}`}
                                onChange={(e) => setStatistic(e.value)}
                                onNodeSelect={(e: any) => {
                                    setAggregation(e.node.aggregation)
                                    if (setUnits) setUnits(e.node.units ?? '')
                                }}
                                filter
                                disabled={disabled}
                                placeholder="Select Metric"/>
                    <ConditionalRender if={statisticError && !statistic}>
                        <small className="text-danger">Metric is Required</small>
                    </ConditionalRender>
                </div>
            </div>
            <div className="w-100"/>
        </>
    )
}

export default Metrics;
